import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import { Button, Tab, Tabs } from '@mui/material';
import Box from '@mui/material/Box';
import { useQueryClient } from '@tanstack/react-query';
import { filter, map } from 'lodash';
import pLimit from 'p-limit';
import React, { ReactElement, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { DialogChromeFooter, DialogChromeHeader, dialogPrimaryActionSx } from 'components/atoms/Dialog/DialogChrome';
import { useUploadFilesContext } from 'components/UploadFilesContext';
import { Permission } from 'interfaces/permissionOption';
import { useS3Upload } from 'services/useS3Upload';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { usePermissions } from 'utils/usePermissions';
import useStudy from 'utils/useStudy';
import ExternalStorageTransferForm from './ExternalStorageTransferForm';
import GenerateCredentials, { CustomerBucketGrantUploadInstructions } from './GenerateCredentials';
import StudyCohortManifestStep from './StudyCohortManifestStep';
import { UploadMethod } from './types';
import UploadFiles from './UploadFiles';
import { UploadMethodChooser, UploadMethodTransferIcon } from './UploadMethodChooser';

const UploadStudyForm = (props: Props): ReactElement => {
  const { onClose, studyId } = props;
  const [selectedMethod, setSelectedMethod] = useState<UploadMethod | null>(null);
  const queryClient = useQueryClient();

  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const { study, isLoading: isStudyLoading } = useStudy(studyId);

  const { getCredentialsAndStartUpload, isLabMetadataLoading } = useS3Upload(labId, studyId);
  const { isPending, setIsPending } = useUploadFilesContext();
  const canCreateCustomerBucketGrants =
    hasPermission(Permission.EditLabCustomerBucketAccessGrants) ||
    hasPermission(Permission.CreateOwnCustomerBucketAccessGrants);
  const enabledExternalSources = filter(
    study?.settings?.externalStorage?.slideSourceBucketSettings || [],
    (setting) => setting.enabled
  );
  const hasExternalSources = enabledExternalSources.length > 0;

  // Reset the upload file context when the modal is closed
  useEffect(() => {
    if (isPending) {
      setIsPending(false);
    }
  }, []);

  const formProps = useForm<IFormValues>({
    resolver: yupResolver(schema),
    defaultValues: { isExternalStorage: false, useExistingStorage: true },
  });

  const { handleSubmit, resetField, reset, setValue } = formProps;

  const closeModal = () => {
    queryClient.removeQueries({ queryKey: ['external-slide-storage', studyId] });
    queryClient.removeQueries({ queryKey: ['external-slide-storage-recursive', studyId] });
    // Invalidate rather than remove: the dashboard is still rendering these, and removal blanks it until the refetch lands.
    queryClient.invalidateQueries({ queryKey: ['study', studyId] });
    queryClient.invalidateQueries({ queryKey: ['studies', labId] });
    queryClient.invalidateQueries({ queryKey: ['labUploadSummary', labId] });
    onClose();
  };

  const formSubmit: SubmitHandler<IFormValues> = async (formValues) => {
    const files = formValues.slides;
    setIsPending(true);
    uploadFiles(files);
    closeModal();
    reset();
  };

  const uploadFiles = async (files: File[]) => {
    const concurrencyLimit = pLimit(10);
    const uploadPromises = map(files, (file) => concurrencyLimit(() => getCredentialsAndStartUpload(file)));

    await Promise.all(uploadPromises);
    // Uploaded files only appear in Lost and Found and the study uploads status once the transfer finishes.
    queryClient.invalidateQueries({ queryKey: ['labUploadSummary', labId] });
    queryClient.invalidateQueries({ queryKey: ['studyUploads', studyId, labId] });
  };

  const selectMethod = (method: UploadMethod) => {
    setSelectedMethod(method);
    if (method === 'external-storage') {
      setValue('isExternalStorage', true);
      resetField('slides');
      return;
    }

    setValue('isExternalStorage', false);
  };

  const returnToMethods = () => {
    setSelectedMethod(null);
    setValue('isExternalStorage', false);
  };

  return (
    <Box
      data-testid="upload-slides-form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: selectedMethod === 'external-storage' ? 'calc(100vh - 64px)' : 'auto',
        maxHeight: 'calc(100vh - 64px)',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box display="flex" flexDirection="column" flex={1} minHeight={0}>
        <DialogChromeHeader
          title="Upload slides"
          subtitle={
            selectedMethod === null
              ? 'Choose an upload method based on the current location of your slides:'
              : undefined
          }
          onClose={closeModal}
        />
        {selectedMethod === null ? (
          <>
            <Box sx={{ px: 3, py: 2, overflowY: 'auto' }}>
              <UploadMethodChooser
                canCreateCustomerBucketGrants={canCreateCustomerBucketGrants}
                hasExternalSources={hasExternalSources}
                isStudyLoading={isStudyLoading}
                onSelect={selectMethod}
              />
            </Box>
            <DialogChromeFooter sticky>
              <Button color="inherit" onClick={closeModal}>
                Cancel
              </Button>
            </DialogChromeFooter>
          </>
        ) : selectedMethod === 'customer-cli' || selectedMethod === 'temporary-credentials' ? (
          <>
            <Tabs
              value={selectedMethod}
              onChange={(_event, method: UploadMethod) => selectMethod(method)}
              aria-label="Slide source"
              sx={{
                mx: 3,
                mt: 1,
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': { textTransform: 'none', minHeight: 56, gap: 1.5 },
              }}
            >
              {canCreateCustomerBucketGrants ? (
                <Tab
                  value="customer-cli"
                  label="From Amazon S3"
                  iconPosition="start"
                  icon={<UploadMethodTransferIcon from={<CloudOutlinedIcon />} to={<CloudOutlinedIcon />} />}
                />
              ) : null}
              <Tab
                value="temporary-credentials"
                label="From this computer"
                iconPosition="start"
                icon={<UploadMethodTransferIcon from={<LaptopMacOutlinedIcon />} to={<CloudOutlinedIcon />} />}
              />
            </Tabs>
            <ScrollableFlowBody>
              {canCreateCustomerBucketGrants ? (
                <Box hidden={selectedMethod !== 'customer-cli'}>
                  <CustomerBucketGrantUploadInstructions studyId={studyId} />
                </Box>
              ) : null}
              <Box hidden={selectedMethod !== 'temporary-credentials'}>
                <GenerateCredentials studyId={studyId} />
              </Box>
            </ScrollableFlowBody>
            <DialogChromeFooter sticky>
              <Button color="inherit" onClick={closeModal}>
                Close
              </Button>
            </DialogChromeFooter>
          </>
        ) : selectedMethod === 'local-files' ? (
          <>
            <FlowHeader onBack={returnToMethods} />
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <UploadFiles formProps={formProps} />
              <Box sx={{ px: 3, pb: 2 }}>
                <StudyCohortManifestStep />
              </Box>
            </Box>
            <DialogChromeFooter sticky>
              <Button onClick={closeModal}>Cancel</Button>
              <Button
                disableElevation
                variant="contained"
                color="primary"
                data-testid="done-button"
                disabled={isPending || isLabMetadataLoading}
                onClick={handleSubmit(formSubmit)}
                sx={dialogPrimaryActionSx}
              >
                {isPending ? 'Uploading' : isLabMetadataLoading ? 'Loading lab settings...' : 'Upload'}
              </Button>
            </DialogChromeFooter>
          </>
        ) : selectedMethod === 'external-storage' ? (
          <>
            <FlowHeader onBack={returnToMethods} />
            <ExternalStorageTransferForm studyId={studyId} onClose={closeModal} />
          </>
        ) : null}
      </Box>
    </Box>
  );
};

const FlowHeader = ({ onBack }: { onBack: () => void }) => (
  <Box sx={{ px: 3, pt: 1.5 }}>
    <Button color="inherit" startIcon={<ArrowBackIcon />} onClick={onBack}>
      Choose another upload method
    </Button>
  </Box>
);

const ScrollableFlowBody = ({ children }: React.PropsWithChildren) => (
  <Box data-testid="upload-slides-scrollable-flow" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 1 }}>
    {children}
  </Box>
);

const schema = yup.object({
  slides: yup.array().of(yup.mixed<File>()).default([]),
  isExternalStorage: yup.boolean().required(),
  useExistingStorage: yup.boolean().required(),
});

interface Props {
  studyId: string;
  onClose: () => void;
}

export interface IFormValues {
  slides: File[];
  isExternalStorage: boolean;
  slideDirectory?: string;
  region?: string;
  accessKeyId?: string;
  secretAccessKey?: string;
  useExistingStorage: boolean;
}

export default UploadStudyForm;
